import React from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";

import {
  transitions,
  positions,
  Provider as AlertProvider,
} from "@blaumaus/react-alert";

import {
  CAN_LIST_R365_REPORTS,
  OFFERLIST,
  CAN_USE_VENDOR_OFFERS,
  SHIPMENTREPORT,
} from "./utils/permissons";

import store from "./store";

import { Provider } from "react-redux";

// Layouts
import Public from "./containers/layouts/Public";
import DefaultLayout from "./containers/layouts/DefaultLayout";
import MarketLayout from "./containers/layouts/MarketLayout";
import ProviderLayout from "./containers/layouts/ProviderLayout";
import Login from "./components/layouts/Login";

// Orders
import OrderListContainer from "./containers/orders/OrderListContainer";
import OrderDetailContainer from "./containers/orders/OrderDetailContainer";

// Purchase Orders
import PurchaseOrderListContainer from "./containers/purchaseOrders/PurchaseOrderListContainer";
import PurchaseOrderCreateContainer from "./containers/purchaseOrders/PurchaseOrderCreateContainer";
import PurchaseOrderDetailContainer from "./containers/purchaseOrders/PurchaseOrderDetailContainer";
import PurchaseOrderByPriceDetailContainer from "./containers/purchaseOrdersByPrice/PurchaseOrderByPriceDetailContainer";
import PurchaseOrderInvoiceDetailContainer from "./containers/purchaseOrders/PurchaseOrderInvoiceDetailContainer";

// Pending Inventory
import PendingInventoryListContainer from "./containers/pendingInventory/PendingInventoryListContainer";
import PendingInventoryDetailContainer from "./containers/pendingInventory/PendingInventoryDetailContainer";

// Price Lists
import PriceListsListContainer from "./containers/priceLists/PriceListsListContainer";
import PriceListDetailContainer from "./containers/priceLists/PriceListDetailContainer";
import PriceListsComparison from "./containers/priceLists/PriceListsComparison";
import PriceListErrorsContainer from "./containers/priceLists/PriceListErrorsContainer";

// Invoices
import InvoiceContainer from "./containers/invoices/InvoiceContainer";
import InvoiceListContainer from "./containers/invoices/InvoiceListContainer";
import LabelsContainer from "./containers/invoices/LabelsContainer";
import ReceiveContainer from "./containers/invoices/ReceiveContainer";

// Inventory
import InventoryDetailContainer from "./containers/inventories/InventoryDetailContainer";
import InventoryLocationsEditContainer from "./containers/inventories/InventoryLocationsEditContainer";
import InventoryAuditListContainer from "./containers/inventories/InventoryAuditListContainer";
import InventoryAuditContainer from "./containers/inventories/InventoryAuditContainer";
import InventoryAuditViewContainer from "./containers/inventories/InventoryAuditViewContainer";

// Credit Notes
import CreditNoteContainer from "./containers/creditNotes/CreditNoteContainer";
import CreditNoteDetailContainer from "./containers/creditNotes/CreditNoteDetailContainer";
import CreditNoteListContainer from "./containers/creditNotes/CreditNoteListContainer";

// Sessions
import SessionsListContainer from "./containers/sessions/SessionsListContainer";
import SessionContainer from "./containers/sessions/SessionContainer";
import ProductionContainer from "./containers/sessions/ProductionContainer";
import ProductionIntermediateLabelsContainer from "./containers/sessions/ProductionIntermediateLabelsContainer";
import ProductionLabelsContainer from "./containers/sessions/ProductionLabelsContainer";
import ViewProductionContainer from "./containers/sessions/ViewProductionContainer";
import EditProductionContainer from "./containers/sessions/EditProductionContainer";

// Shipment
import ShipmentListContainer from "./containers/shipments/ShipmentListContainer";
import ShipmentNewContainer from "./containers/shipments/ShipmentNewContainer";
import ShipmentEditContainer from "./containers/shipments/ShipmentEditContainer";
import ShipmentDetailContainer from "./containers/shipments/ShipmentDetailContainer";

// Returns
import ReturnListContainer from "./containers/returns/ReturnListContainer";
import ReturnContainer from "./containers/returns/ReturnContainer";
import ReturnDetailContainer from "./containers/returns/ReturnDetailContainer";
import ReturnAddInventoryContainer from "./containers/returns/ReturnAddInventoryContainer";

// Market Inventory
import BarcodeScannerContainer from "./containers/marketInventory/BarcodeScannerContainer";
import StockLevelsDashboardContainer from "./containers/marketInventory/StockLevelsDashboardContainer";
import AlertsPanelContainer from "./containers/marketInventory/AlertsPanelContainer";
import ConsumptionHistoryContainer from "./containers/marketInventory/ConsumptionHistoryContainer";

// Maintenance
import MaintenanceDashboardContainer from "./containers/maintenance/MaintenanceDashboardContainer";
import ScheduledTasksDashboardContainer from "./containers/maintenance/ScheduledTasksDashboardContainer";
import MachinesListContainer from "./containers/maintenance/MachinesListContainer";
import MachineDetailContainer from "./containers/maintenance/MachineDetailContainer";
import MachineFormContainer from "./containers/maintenance/MachineFormContainer";
import FacilitiesListContainer from "./containers/maintenance/FacilitiesListContainer";
import FacilityDetailContainer from "./containers/maintenance/FacilityDetailContainer";
import FacilityFormContainer from "./containers/maintenance/FacilityFormContainer";
import TaskTemplatesListContainer from "./containers/maintenance/TaskTemplatesListContainer";
import TaskTemplateFormContainer from "./containers/maintenance/TaskTemplateFormContainer";
import TaskAssignmentsListContainer from "./containers/maintenance/TaskAssignmentsListContainer";
import TaskAssignmentFormContainer from "./containers/maintenance/TaskAssignmentFormContainer";
import MaintenanceRecordsListContainer from "./containers/maintenance/MaintenanceRecordsListContainer";
import MaintenanceRecordDetailContainer from "./containers/maintenance/MaintenanceRecordDetailContainer";
import MaintenanceRecordFormContainer from "./containers/maintenance/MaintenanceRecordFormContainer";
import MaintenanceSettingsContainer from "./containers/maintenance/MaintenanceSettingsContainer";

// Reports
import InventoryByItemListContainer from "./containers/reports/InventoryByItemListContainer";
import InventoryListContainer from "./containers/reports/InventoryListContainer";
import Report_R365 from "./containers/reports/Report_R365";
import ReportExpiredContainer from "./containers/reports/ReportExpiredContainer";
import ReportIngredientsContainer from "./containers/reports/ReportIngredientsContainer";
import ReportInvoicesContainer from "./containers/reports/ReportInvoicesContainer";
import ReportProductionContainer from "./containers/reports/ReportProductionContainer";
import ReportReceivingContainer from "./containers/reports/ReportReceivingContainer";
import ReportShipmentContainer from "./containers/reports/ReportShipmentContainer";
import ReportRecallContainer from "./containers/reports/ReportRecallContainer";
import ReportWasteContainer from "./containers/reports/ReportWasteContainer";
import ReportYieldContainer from "./containers/reports/ReportYieldContainer";
import WeightedPriceContainer from "./containers/reports/WeightedPriceContainer";

import { AlertTemplate } from "./utils/Helpers";
import { SUPER_USER, PLANT_USER, MARKET_USER } from "./utils/AuthService";

// Provider Portal
import ProviderDashboardContainer from "./containers/providerPortal/ProviderDashboardContainer";
import ProviderTaskListContainer from "./containers/providerPortal/ProviderTaskListContainer";
import ProviderTaskDetailContainer from "./containers/providerPortal/ProviderTaskDetailContainer";

const options = {
  // you can also just use 'bottom center'
  position: positions.TOP_CENTER,
  timeout: 10000,
  offset: "30px",
  // you can also just use 'scale'
  transition: transitions.SCALE,
  containerStyle: {
    marginTop: "30px",
    width: "100%",
    zIndex: 4,
  },
};

const App = (appProps) => (
  <Provider store={store}>
    <AlertProvider template={AlertTemplate} {...options}>
      <Router>
        <div className="app">
          <Routes>
            {/* Orders */}
            <Route element={<MarketLayout />}>
              <Route
                path="/orders"
                element={<OrderListContainer {...appProps} />}
              />
              <Route
                path="/orders/:id"
                element={<OrderDetailContainer {...appProps} />}
              />
            </Route>
            {/* Purchase Orders (for market and plant users)*/}
            <Route
              element={
                <DefaultLayout
                  permissionsRequired={[
                    { model: OFFERLIST, perms: [CAN_USE_VENDOR_OFFERS] },
                  ]}
                  isFeatureFlag
                  rolesRequired={[PLANT_USER, MARKET_USER]}
                  {...appProps}
                />
              }
            >
              <Route
                path="/purchase_orders_by_price/:id"
                element={<PurchaseOrderByPriceDetailContainer {...appProps} />}
              />
            </Route>
            <Route
              element={
                <DefaultLayout rolesRequired={[PLANT_USER, MARKET_USER]} />
              }
            >
              <Route
                path="/purchase_orders"
                element={<PurchaseOrderListContainer {...appProps} />}
              />
              <Route
                path="/purchase_orders/:id"
                element={<PurchaseOrderDetailContainer {...appProps} />}
              />
            </Route>
            {/* Purchase Orders (only for plant users)*/}
            <Route element={<DefaultLayout />}>
              <Route
                path="/purchase_orders/new"
                element={<PurchaseOrderCreateContainer {...appProps} />}
              />
              <Route
                path="/purchase_orders/:id/invoice_create"
                element={<ReceiveContainer {...appProps} />}
              />
              <Route
                path="/purchase_orders/:id/invoice"
                element={<PurchaseOrderInvoiceDetailContainer {...appProps} />}
              />
              <Route
                path="/pending_inventory"
                element={<PendingInventoryListContainer {...appProps} />}
              />
              <Route
                path="/pending_inventory/:id"
                element={<PendingInventoryDetailContainer {...appProps} />}
              />
            </Route>

            {/* Price Lists */}
            <Route
              element={
                <DefaultLayout
                  permissionsRequired={[
                    { model: OFFERLIST, perms: [CAN_USE_VENDOR_OFFERS] },
                  ]}
                  isFeatureFlag
                  {...appProps}
                />
              }
            >
              <Route
                path="/price_lists"
                element={<PriceListsListContainer {...appProps} />}
              />
              <Route
                path="/price_lists/:id"
                element={<PriceListDetailContainer {...appProps} />}
              />
              <Route
                path="/price_lists/:id/errors"
                element={<PriceListErrorsContainer {...appProps} />}
              />
              <Route
                path="/compare_price_lists"
                element={<PriceListsComparison {...appProps} />}
              />
            </Route>
            {/* Invoices */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/invoice"
                element={<InvoiceListContainer {...appProps} />}
              />
              <Route
                path="/invoice/new"
                element={<ReceiveContainer {...appProps} />}
              />
              <Route
                path="/invoice/:id"
                element={<InvoiceContainer {...appProps} />}
              />
              <Route
                path="/invoice/:id/labels"
                element={<LabelsContainer {...appProps} />}
              />
            </Route>
            {/* Credit Notes */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/credit_note"
                element={<CreditNoteListContainer {...appProps} />}
              />
              <Route
                path="/credit_note/new"
                element={<CreditNoteContainer {...appProps} />}
              />
              <Route
                path="/credit_note/:id"
                element={<CreditNoteDetailContainer {...appProps} />}
              />
            </Route>
            {/* Sessions */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/session"
                element={<SessionsListContainer {...appProps} />}
              />
              <Route
                path="/session/new"
                element={<SessionContainer {...appProps} />}
              />
              <Route
                path="/session/:id"
                element={<ViewProductionContainer {...appProps} />}
              />
              <Route
                path="/session/:id/edit"
                element={<ProductionContainer {...appProps} />}
              />
              <Route
                path="/session/:id/edit_finished"
                element={<EditProductionContainer {...appProps} />}
              />
              <Route
                path="/session/:id/intermediate_labels"
                element={
                  <ProductionIntermediateLabelsContainer {...appProps} />
                }
              />
              <Route
                path="/session/:id/labels"
                element={<ProductionLabelsContainer {...appProps} />}
              />
            </Route>
            {/* Shipments */}

            <Route element={<DefaultLayout />}>
              <Route
                path="/shipment"
                element={<ShipmentListContainer {...appProps} />}
              />
              <Route
                path="/shipment/new"
                element={<ShipmentNewContainer {...appProps} />}
              />
              <Route
                path="/shipment/:id"
                element={<ShipmentDetailContainer {...appProps} />}
              />
              <Route
                path="/shipment/:id/edit"
                element={<ShipmentEditContainer {...appProps} />}
              />
            </Route>
            {/* Market Inventory (for market users) */}
            <Route element={<MarketLayout />}>
              <Route
                path="/market-inventory/scanner"
                element={<BarcodeScannerContainer {...appProps} />}
              />
              <Route
                path="/market-inventory/stock-levels"
                element={<StockLevelsDashboardContainer {...appProps} />}
              />
              <Route
                path="/market-inventory/alerts"
                element={<AlertsPanelContainer {...appProps} />}
              />
              <Route
                path="/market-inventory/consumption"
                element={<ConsumptionHistoryContainer {...appProps} />}
              />
            </Route>
            {/* Maintenance */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/maintenance"
                element={<MaintenanceDashboardContainer {...appProps} />}
              />
              <Route
                path="/maintenance/tasks"
                element={<ScheduledTasksDashboardContainer {...appProps} />}
              />
              <Route
                path="/maintenance/machines"
                element={<MachinesListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/machines/new"
                element={<MachineFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/machines/:id"
                element={<MachineDetailContainer {...appProps} />}
              />
              <Route
                path="/maintenance/machines/:id/edit"
                element={<MachineFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/facilities"
                element={<FacilitiesListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/facilities/new"
                element={<FacilityFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/facilities/:id"
                element={<FacilityDetailContainer {...appProps} />}
              />
              <Route
                path="/maintenance/facilities/:id/edit"
                element={<FacilityFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-templates"
                element={<TaskTemplatesListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-templates/new"
                element={<TaskTemplateFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-templates/:id"
                element={<TaskTemplateFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-templates/:id/edit"
                element={<TaskTemplateFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-assignments"
                element={<TaskAssignmentsListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-assignments/new"
                element={<TaskAssignmentFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/task-assignments/:id/edit"
                element={<TaskAssignmentFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records"
                element={<MaintenanceRecordsListContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records/new"
                element={<MaintenanceRecordFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records/service-visit"
                element={<MaintenanceRecordFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records/:id"
                element={<MaintenanceRecordDetailContainer {...appProps} />}
              />
              <Route
                path="/maintenance/records/:id/edit"
                element={<MaintenanceRecordFormContainer {...appProps} />}
              />
              <Route
                path="/maintenance/settings"
                element={<MaintenanceSettingsContainer {...appProps} />}
              />
              <Route
                path="/maintenance/service-providers"
                element={<MaintenanceSettingsContainer {...appProps} />}
              />
            </Route>
            {/* Returns */}

            <Route element={<DefaultLayout />}>
              <Route
                path="/return"
                element={<ReturnListContainer {...appProps} />}
              />
              <Route
                path="/return/new"
                element={<ReturnContainer {...appProps} />}
              />
              <Route
                path="/return/:id"
                element={<ReturnDetailContainer {...appProps} />}
              />
              <Route
                path="/return/:id/add_inventory"
                element={<ReturnAddInventoryContainer {...appProps} />}
              />
            </Route>
            {/* Reports */}
            <Route element={<DefaultLayout rolesRequired={[SUPER_USER]} />}>
              <Route
                path="/reports/inventory"
                element={<InventoryListContainer {...appProps} />}
              />
              <Route
                path="/reports/inventory_items"
                element={<InventoryByItemListContainer {...appProps} />}
              />
              <Route
                path="/reports/shipment"
                element={<ReportShipmentContainer {...appProps} />}
              />
              <Route
                path="/reports/recall"
                element={<ReportRecallContainer {...appProps} />}
              />
              <Route
                path="/reports/invoices"
                element={<ReportInvoicesContainer {...appProps} />}
              />
              <Route
                path="/reports/production"
                element={<ReportProductionContainer {...appProps} />}
              />
              <Route
                path="/reports/waste"
                element={<ReportWasteContainer {...appProps} />}
              />
              <Route
                path="/reports/yield"
                element={<ReportYieldContainer {...appProps} />}
              />
              <Route
                path="/reports/expired_inventories"
                element={<ReportExpiredContainer {...appProps} />}
              />
              <Route
                path="/reports/ingredients"
                element={<ReportIngredientsContainer {...appProps} />}
              />
              <Route
                path="/reports/weighted_price"
                element={<WeightedPriceContainer {...appProps} />}
              />
              <Route
                path="/reports/receiving"
                element={<ReportReceivingContainer {...appProps} />}
              />
            </Route>
            <Route
              element={
                <DefaultLayout
                  rolesRequired={[SUPER_USER]}
                  permissionsRequired={[
                    { model: SHIPMENTREPORT, perms: [CAN_LIST_R365_REPORTS] },
                  ]}
                />
              }
            >
              <Route
                path="/reports/R365"
                element={<Report_R365 {...appProps} />}
              />
            </Route>
            {/* Inventories */}
            <Route element={<DefaultLayout />}>
              <Route
                path="/inventory/audit"
                element={<InventoryAuditListContainer {...appProps} />}
              />
              <Route
                path="/inventory/locations"
                element={<InventoryLocationsEditContainer {...appProps} />}
              />
              <Route
                path="/inventory/:id"
                element={<InventoryDetailContainer {...appProps} />}
              />
              <Route
                path="/inventory/audit/:id/edit"
                element={<InventoryAuditContainer {...appProps} />}
              />
              <Route
                path="/inventory/audit/:id"
                element={<InventoryAuditViewContainer {...appProps} />}
              />
            </Route>
            {/* Provider Portal */}
            <Route element={<ProviderLayout />}>
              <Route
                path="/provider-portal"
                element={<ProviderDashboardContainer {...appProps} />}
              />
              <Route
                path="/provider-portal/tasks"
                element={<ProviderTaskListContainer {...appProps} />}
              />
              <Route
                path="/provider-portal/tasks/:id"
                element={<ProviderTaskDetailContainer {...appProps} />}
              />
            </Route>
            {/* Login */}
            <Route
              path="/login"
              element={<Public component={Login} {...appProps} />}
            />
            {/* Default */}
            <Route path="*" element={<Navigate to="/orders" />} />
          </Routes>
        </div>
      </Router>
    </AlertProvider>
  </Provider>
);

export default App;
